import { adminFetch } from './adminApi';

import { resolveUpload } from './uploads';

const BACKEND_URL = (process.env.REACT_APP_BACKEND_URL || '').replace(/\/+$/, '');
const API = `${BACKEND_URL}/api`;

/**
 * Banner data layer — adapter between the admin UI's flat form object and the
 * backend's stored shape.
 *
 * The API keeps everything common to all types (name, type, placement,
 * category, schedule, priority, status) at the top level, and everything that
 * is presentation-only inside a `content` JSON blob. That is the right call —
 * it is why a new banner type needs no migration — but a form is flat, so the
 * translation lives here and nowhere else.
 *
 * Verified live 2026-08-06: 7 routes deployed, auth guards return 401 on all
 * admin paths, `GET /api/banners` is public and returns `{banners, count}`,
 * tracking an unknown id returns 404.
 *
 * ⚠️ The OpenAPI spec declares NO request body schema for POST/PUT (they come
 * back `null`), so the exact key names below could not be verified against the
 * spec — they follow the backend team's written description. Both read paths
 * are therefore tolerant: `fromApi` looks for each value at the top level AND
 * inside `content`, so if the split differs from what we assumed, the table and
 * form still populate rather than silently rendering blanks.
 */

/**
 * Re-exported from lib/uploads, which is where this now lives: gift-card artwork
 * hit the same relative-path bug the day admin uploads shipped, and two copies
 * of a URL-resolution rule is how they drift.
 *
 * The raw path is still what gets written back on save — storing the absolute
 * URL would put the API host in the database.
 */
export { resolveUpload };

/** Keys the API keeps at the top level. Everything else goes into `content`. */
const TOP_LEVEL = new Set([
  'name', 'type', 'placement', 'category_id', 'status', 'priority',
  'starts_at', 'ends_at', 'desktop_image', 'mobile_image',
]);

export class BannerApiUnavailable extends Error {
  constructor() {
    super('The banner API is not available on this backend yet.');
    this.name = 'BannerApiUnavailable';
    this.unavailable = true;
  }
}

async function call(path, options) {
  try {
    return await adminFetch(path, options);
  } catch (error) {
    if (/\b404\b|not found/i.test(error.message || '')) throw new BannerApiUnavailable();
    throw error;
  }
}

/* ── Shape translation ───────────────────────────────────────────────────── */

/** API record → flat object the form and table read. */
export function fromApi(raw) {
  if (!raw) return null;
  const content = raw.content || {};
  // Top level wins; `content` is the fallback. Covers either split.
  const pick = (key) => (raw[key] !== undefined && raw[key] !== null ? raw[key] : content[key]);

  return {
    banner_id: raw.banner_id ?? raw.id,
    name: raw.name ?? '',
    type: raw.type,
    placement: raw.placement,
    category_id: raw.category_id ?? null,
    category_name: raw.category_name ?? null,
    status: raw.status ?? 'draft',
    // The server folds the schedule in for us — do not recompute it locally.
    effective_status: raw.effective_status ?? null,
    priority: raw.priority ?? 0,
    // API spells these `starts_at` / `ends_at`; the older names are read too so
    // a cached response does not blank the schedule.
    starts_at: raw.starts_at ?? raw.start_at ?? null,
    ends_at: raw.ends_at ?? raw.end_at ?? null,
    views: raw.views ?? null,
    clicks: raw.clicks ?? null,
    ctr: raw.ctr ?? null,
    updated_at: raw.updated_at ?? null,
    updated_by: raw.updated_by ?? raw.updated_by_name ?? null,

    desktop_image: pick('desktop_image') ?? '',
    mobile_image: pick('mobile_image') ?? '',
    desktop_image_url: resolveUpload(pick('desktop_image')),
    mobile_image_url: resolveUpload(pick('mobile_image')),
    title: pick('title') ?? '',
    subtitle: pick('subtitle') ?? '',
    cta_label: pick('cta_label') ?? '',
    cta_url: pick('cta_url') ?? '',
    text_align: pick('text_align') ?? 'left',
    overlay: pick('overlay') ?? 0,
    background_color: pick('background_color') ?? '',
    text_color: pick('text_color') ?? '',
    icon: pick('icon') ?? '',
  };
}

/**
 * Flat form object → API body.
 *
 * Only keys actually present are emitted, because PUT is a partial update: the
 * backend merges `content` rather than replacing it, so sending `subtitle: ""`
 * for a field the admin never touched would wipe it. `undefined` is dropped;
 * an explicit empty string is kept, since that is how a field gets cleared.
 */
export function toApi(values, { partial = false } = {}) {
  const body = {};
  const content = {};

  for (const [key, value] of Object.entries(values)) {
    if (value === undefined) continue;
    if (['banner_id', 'effective_status', 'views', 'clicks', 'ctr', 'updated_at',
         'updated_by', 'category_name', 'type_label',
         'desktop_image_url', 'mobile_image_url'].includes(key)) continue;

    if (TOP_LEVEL.has(key)) body[key] = value;
    else content[key] = value;
  }

  if (body.priority !== undefined) body.priority = Number(body.priority) || 0;
  if (content.overlay !== undefined && content.overlay !== '') content.overlay = Number(content.overlay);
  if (body.starts_at === '') body.starts_at = null;
  if (body.ends_at === '') body.ends_at = null;

  // On a partial update an empty `content` means "change nothing presentational".
  if (!partial || Object.keys(content).length) body.content = content;
  return body;
}

/**
 * Builds a multipart body when any image slot holds a File.
 *
 * There is no separate upload endpoint — create and update accept multipart
 * directly — so files travel with the record. `content` rides along as a JSON
 * string because multipart has no nested objects.
 */
function toFormData(values, { partial } = {}) {
  const body = toApi(values, { partial });
  const fd = new FormData();

  for (const [key, value] of Object.entries(body)) {
    if (key === 'content') { fd.append('content', JSON.stringify(value)); continue; }
    if (value === null) continue;
    fd.append(key, value instanceof File ? value : String(value));
  }
  return fd;
}

const hasFile = (values) =>
  ['desktop_image', 'mobile_image'].some((k) => values[k] instanceof File);

/* ── Meta ────────────────────────────────────────────────────────────────── */

/**
 * Server-owned types, placements and categories. When this answers, it is the
 * authority — the local registry in bannerTypes.js becomes a fallback for when
 * the call fails, so placement slugs can never drift out of sync again.
 */
export async function fetchBannerMeta() {
  const data = await call('/admin/banners/meta');
  return {
    types: data?.types || data?.banner_types || [],
    placements: data?.placements || [],
    categories: data?.categories || [],
  };
}

/* ── CRUD ────────────────────────────────────────────────────────────────── */

export async function listBanners(params = {}) {
  const qs = new URLSearchParams();
  for (const [k, v] of Object.entries(params)) {
    if (v !== undefined && v !== null && v !== '' && v !== 'all') qs.set(k, String(v));
  }
  const data = await call(`/admin/banners${qs.toString() ? `?${qs}` : ''}`);
  const list = data?.banners || data?.items || (Array.isArray(data) ? data : []);
  return {
    banners: list.map(fromApi),
    total: data?.total ?? data?.count ?? list.length,
    stats: data?.stats ?? null,
  };
}

export const getBanner = (id) => call(`/admin/banners/${id}`).then(fromApi);

/**
 * Workaround for a live backend bug: a multipart save stores `content` as `{}`.
 *
 * Confirmed 2026-08-06 — a Category Hero created with title, subtitle, CTA,
 * alignment, overlay and background colour came back with `content: {}` while
 * both images uploaded correctly. The JSON path is unaffected; only the
 * multipart branch loses it, so the `content` form part is evidently not being
 * parsed back into JSON server-side.
 *
 * So when a multipart save carried content and the response does not show it,
 * a second JSON PUT writes it. Two requests instead of one, and it becomes a
 * no-op the moment the backend parses the part — `sent` will match what came
 * back and the follow-up is skipped.
 */
async function saveWithContentFallback(path, method, values, { partial } = {}) {
  const sent = toApi(values, { partial });
  const wantsContent = sent.content && Object.keys(sent.content).length > 0;

  const saved = await call(path, { method, body: toFormData(values, { partial }) });
  const stored = saved?.content || {};

  if (wantsContent && Object.keys(stored).length === 0) {
    const id = saved?.banner_id ?? saved?.id;
    if (id) {
      const repaired = await call(`/admin/banners/${id}`, {
        method: 'PUT', body: { content: sent.content },
      });
      return fromApi(repaired ?? saved);
    }
  }
  return fromApi(saved);
}

export const createBanner = (values) =>
  hasFile(values)
    ? saveWithContentFallback('/admin/banners', 'POST', values)
    : call('/admin/banners', { method: 'POST', body: toApi(values) }).then(fromApi);

export const updateBanner = (id, values) =>
  hasFile(values)
    ? saveWithContentFallback(`/admin/banners/${id}`, 'PUT', values, { partial: true })
    : call(`/admin/banners/${id}`, { method: 'PUT', body: toApi(values, { partial: true }) }).then(fromApi);

export const deleteBanner = (id) => call(`/admin/banners/${id}`, { method: 'DELETE' });

/** Dedicated toggle — cheaper than a full PUT and cannot disturb `content`. */
export const setBannerStatus = (id, status) =>
  call(`/admin/banners/${id}/status`, { method: 'PUT', body: { status } }).then(fromApi);

/* ── Composed operations ─────────────────────────────────────────────────── */
/*
 * The backend has no duplicate / bulk / reorder routes. Rather than disable
 * working UI, each is composed from the endpoints that do exist. They are
 * honest about the cost: N requests instead of one, run with a small
 * concurrency cap, and partial failure is reported rather than swallowed.
 *
 * Worth asking the backend for a real `POST /admin/banners/bulk` if admins
 * routinely select 20+ rows — until then this is correct, just chattier.
 */

async function inBatches(items, fn, size = 4) {
  const failures = [];
  for (let i = 0; i < items.length; i += size) {
    const slice = items.slice(i, i + size);
    const results = await Promise.allSettled(slice.map(fn));
    results.forEach((r, j) => { if (r.status === 'rejected') failures.push(slice[j]); });
  }
  if (failures.length) {
    throw new Error(`${failures.length} of ${items.length} could not be updated.`);
  }
}

export async function duplicateBanner(id) {
  const original = await getBanner(id);
  const copy = { ...original, name: `${original.name} (copy)`, status: 'draft' };
  delete copy.banner_id; delete copy.views; delete copy.clicks;
  delete copy.effective_status; delete copy.updated_at; delete copy.updated_by;
  return createBanner(copy);
}

export const bulkBanners = (action, ids) =>
  action === 'delete'
    ? inBatches(ids, (id) => deleteBanner(id))
    : inBatches(ids, (id) => setBannerStatus(id, action === 'enable' ? 'active' : 'inactive'));

/** Rewrites `priority` so the given order is what the storefront serves. */
export const reorderBanners = (_placement, orderedIds) => {
  const top = orderedIds.length;
  return inBatches(
    orderedIds.map((id, i) => ({ id, priority: top - i })),
    ({ id, priority }) => call(`/admin/banners/${id}`, { method: 'PUT', body: { priority } })
  );
};

/* ── Storefront ──────────────────────────────────────────────────────────── */

export async function fetchPlacementBanners(placement, extra = {}) {
  try {
    const qs = new URLSearchParams({ placement, ...extra });
    const res = await fetch(`${API}/banners?${qs}`, { credentials: 'include' });
    if (!res.ok) return [];
    const data = await res.json();
    return (data?.banners || []).map(fromApi);
  } catch {
    return [];
  }
}

export function trackBannerEvent(bannerId, event) {
  try {
    const url = `${API}/banners/${bannerId}/${event === 'click' ? 'click' : 'view'}`;
    if (navigator.sendBeacon) navigator.sendBeacon(url);
    else fetch(url, { method: 'POST', keepalive: true, credentials: 'include' }).catch(() => {});
  } catch { /* analytics must never break a page */ }
}
